<template>
  <div>
    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)">
      <div class="notice__body">{{ error }}</div>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("editor.details") }}</h2>
      <p class="card__sub">{{ t("editor.detailsLede") }}</p>
      <div class="grid grid--2" style="margin-top: var(--space-4)">
        <div class="field">
          <label class="field__label" for="o-name">{{ t("apps.nameLabel") }} <span class="req">*</span></label>
          <input id="o-name" v-model.trim="form.name" class="input" maxlength="120" :disabled="archived" />
        </div>
        <div class="field">
          <label class="field__label" for="o-cat">{{ t("editor.category") }}</label>
          <input id="o-cat" v-model.trim="form.category" class="input" maxlength="64" :disabled="archived" />
        </div>
        <div class="field">
          <span class="field__label">{{ t("editor.icon") }}</span>
          <ImageUpload v-model="form.icon_url" kind="icon" :app-id="app.id" :label="t('editor.iconUrl')" :disabled="archived" />
        </div>
        <div class="field">
          <label class="field__label" for="o-mode">{{ t("editor.listingMode") }}</label>
          <select id="o-mode" v-model="form.listing_mode" class="select" :disabled="archived">
            <option value="public">{{ t("editor.listingModes.public") }}</option>
            <option value="private">{{ t("editor.listingModes.private") }}</option>
          </select>
          <span class="field__hint">{{ t("editor.listingModeHint") }}</span>
        </div>
      </div>
      <div class="row" style="margin-top: var(--space-4)">
        <button class="btn" :disabled="busy || archived || !form.name || (form.icon_url && !isHttps(form.icon_url))" @click="save">
          <span v-if="busy" class="spinner" aria-hidden="true"></span>
          {{ busy ? t("common.saving") : t("common.save") }}
        </button>
        <span v-if="saved" class="badge badge--success">{{ t("common.saved") }}</span>
      </div>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("editor.archiveTitle") }}</h2>
      <p v-if="archived" class="small muted">{{ t("editor.archivedBody") }}</p>
      <template v-else>
        <p class="small muted">{{ t("editor.archiveBody") }}</p>
        <button class="btn btn--danger" :disabled="busy" @click="archive">{{ t("editor.archive") }}</button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText } from "../../lib/errors";
import { isHttps } from "../../lib/media";
import ImageUpload from "../ImageUpload.vue";

const props = defineProps({ app: { type: Object, required: true } });
const emit = defineEmits(["changed"]);
const { t } = useI18n();
const errorText = useErrorText();

const form = reactive({ name: "", category: "", icon_url: "", listing_mode: "public" });
const busy = ref(false);
const saved = ref(false);
const error = ref("");
const archived = computed(() => props.app.status === "archived");

watch(
  () => props.app,
  (app) => Object.assign(form, {
    name: app.name || "",
    category: app.category || "",
    icon_url: app.icon_url || "",
    listing_mode: app.listing_mode === "private" ? "private" : "public"
  }),
  { immediate: true }
);

async function run(fn) {
  busy.value = true;
  error.value = "";
  try {
    await fn();
    emit("changed");
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

const save = () => run(async () => {
  await api.updateApp(props.app.id, {
    name: form.name,
    category: form.category || null,
    icon_url: form.icon_url || null,
    listing_mode: form.listing_mode
  });
  saved.value = true;
  setTimeout(() => (saved.value = false), 2500);
});

const archive = () => {
  if (!window.confirm(t("editor.archiveConfirm", { name: props.app.name }))) return;
  return run(() => api.updateApp(props.app.id, { status: "archived" }));
};
</script>
